/**
 * Age calculator — pure date math.
 *
 * Functions take ISO date strings ("YYYY-MM-DD") and an explicit "at" date, so
 * they are deterministic and unit-testable (the component passes today's ISO).
 * Calendar arithmetic uses integer Y/M/D + `Date.UTC` (no timezone/DST drift,
 * no `Date.now()`).
 */

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export interface DateParts {
  y: number;
  m: number; // 1-12
  d: number;
}

const DAY_MS = 86_400_000;

export function parseISO(iso: string): DateParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > daysInMonth(y, m)) return null;
  return { y, m, d };
}

export function daysInMonth(year: number, month: number): number {
  // month is 1-12; Date.UTC(year, month, 0) gives the last day of `month`.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export interface AgeResult {
  years: number;
  months: number;
  days: number;
  totalDays: number;
  totalWeeks: number;
  totalMonths: number;
  totalHours: number;
  weekday: string;
  daysToNextBirthday: number;
  nextBirthdayISO: string;
}

/** Full age breakdown of `birthISO` as of `atISO`. Returns null on invalid
 * input or when the birth date is after the reference date. */
export function calculateAge(
  birthISO: string,
  atISO: string,
): AgeResult | null {
  const b = parseISO(birthISO);
  const a = parseISO(atISO);
  if (!b || !a) return null;

  const bUTC = Date.UTC(b.y, b.m - 1, b.d);
  const aUTC = Date.UTC(a.y, a.m - 1, a.d);
  if (bUTC > aUTC) return null;

  let years = a.y - b.y;
  let months = a.m - b.m;
  let days = a.d - b.d;

  if (days < 0) {
    months -= 1;
    // Borrow the length of the month preceding `at`.
    let pm = a.m - 1;
    let py = a.y;
    if (pm === 0) {
      pm = 12;
      py -= 1;
    }
    days += daysInMonth(py, pm);
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const totalDays = Math.round((aUTC - bUTC) / DAY_MS);

  // Next birthday on or after `at`.
  let nb = Date.UTC(a.y, b.m - 1, b.d);
  if (nb < aUTC) nb = Date.UTC(a.y + 1, b.m - 1, b.d);
  const daysToNextBirthday = Math.round((nb - aUTC) / DAY_MS);
  const nbDate = new Date(nb);
  const nextBirthdayISO = `${nbDate.getUTCFullYear()}-${String(
    nbDate.getUTCMonth() + 1,
  ).padStart(2, "0")}-${String(nbDate.getUTCDate()).padStart(2, "0")}`;

  return {
    years,
    months,
    days,
    totalDays,
    totalWeeks: Math.floor(totalDays / 7),
    totalMonths: years * 12 + months,
    totalHours: totalDays * 24,
    weekday: WEEKDAYS[new Date(bUTC).getUTCDay()],
    daysToNextBirthday,
    nextBirthdayISO,
  };
}
